import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { IconChevronRight } from '../../mobile/components/Icons'
import { DangerConfirm } from './DangerConfirm'
import type { AccessVerdict } from '../../lib/orgAccess'
import type { OrgInviteRow, OrgMemberRow } from '../../api/endpoints'

/**
 * One roster row. Members and pending invites share a list — because the seat
 * ledger counts `used + pendingInvites` against `limit`, and an owner reading
 * two separate lists has to do the addition the 402 will later do for them —
 * but they are two lifecycles with two verbs, so the row states which:
 * **Remove** a person, **Revoke** an invite.
 *
 * The second line is the point of the whole screen (see `lib/orgAccess.ts`):
 * not "joined Mar 2026" but what this person can see right now.
 *
 * ── EXPANSION IS IN PLACE ─────────────────────────────────────────────────
 * Tap-to-expand rather than navigate-to-detail, so a 12-person offboarding
 * audit never loses its place. `role="button" + aria-expanded + aria-controls`,
 * Enter AND Space, and the actions are real `<button>`s inside the controlled
 * region so they enter the tab order right after their row.
 *
 * ── NO BUTTON THAT ALWAYS 409s ────────────────────────────────────────────
 * The owner's row renders no destructive control at all, and says why. The
 * server would refuse it (`seats.ts::mayRemoveMember`); rendering the button
 * anyway and letting the user discover the rule by pressing it is the cheap
 * version of this screen.
 *
 * There is deliberately NO "copy invite link": `invites.ts` stores only a
 * `sha256`, so the raw secret exists in the email and nowhere else. An
 * affordance that cannot work is worse than no affordance.
 */

type Busy = null | 'role' | 'remove' | 'revoke' | 'transfer' | 'resend'

function Avatar({ label }: { label: string }) {
  // Initials, deliberately. A generative crest is right for the ORG and wrong
  // for 12 human colleagues — that is the NFT-avatar failure, and initials are
  // how a person recognises their coworker.
  const initials = label
    .replace(/@.*$/, '')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('') || '?'
  return (
    <span
      aria-hidden="true"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream text-[12px] font-bold tracking-tight text-primary"
    >
      {initials}
    </span>
  )
}

function RoleBadge({ role, pending }: { role: string | null; pending?: boolean }) {
  if (pending) {
    return (
      <span className="shrink-0 rounded-full border border-gold-500/45 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-gold-600">
        Invited
      </span>
    )
  }
  if (role === 'owner') {
    return (
      <span className="shrink-0 rounded-full bg-gold-500/15 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-gold-600">
        Owner
      </span>
    )
  }
  if (role === 'admin') {
    return (
      <span className="shrink-0 rounded-full bg-navy/[0.06] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-soft">
        Admin
      </span>
    )
  }
  return <span className="shrink-0 text-[11px] uppercase tracking-wider text-soft">Member</span>
}

const TONE_CLASS: Record<AccessVerdict['tone'], string> = {
  normal: 'text-soft',
  muted: 'text-soft',
  warning: 'text-warning',
}

/**
 * A member's VERIFICATION STATUS at a glance — the chip Tej asked for, distinct
 * from the RoleBadge (owner/admin/member). It reads the effective state, not a
 * raw flag: an ACTIVE member of an approved org inherits verification (that
 * inheritance is the whole product — see lib/orgAccess.ts), a suspended row is
 * paused or, when a reviewer said no to THEM specifically, declined, and an
 * active member of an unverified org is pending. `access` already resolved
 * exactly this for the sentence below the name, so the chip cannot disagree
 * with it. `suspendedReason` is optional on the row (older rows lack it) — the
 * chip degrades to a plain "Paused" without it.
 */
function VerifyChip({
  status, suspendedReason, access,
}: {
  status: string
  suspendedReason: string | null
  access: AccessVerdict
}) {
  let text: string
  let cls: string
  if (status === 'suspended') {
    text = suspendedReason === 'kyc_rejected' ? 'Declined' : 'Paused'
    cls = 'border-danger/40 text-danger'
  } else if (access.tone === 'warning') {
    // Active, but the company's verification was declined / not in.
    text = access.segment === 'paused' ? 'Paused' : 'Pending'
    cls = 'border-warning/45 text-warning'
  } else {
    text = 'Verified'
    cls = 'border-gold-500/45 text-gold-600'
  }
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider ${cls}`}
      data-testid="team-verify-chip"
    >
      {text}
    </span>
  )
}

export function MemberRow({
  member,
  access,
  viewerRole,
  viewerUid,
  orgName,
  open,
  onToggle,
  onSetRole,
  onRemove,
  onLeave,
  onTransfer,
  error,
  busy,
  className = '',
  style,
}: {
  member: OrgMemberRow
  access: AccessVerdict
  viewerRole: 'owner' | 'admin' | 'member' | null
  viewerUid: string | null
  orgName: string
  open: boolean
  onToggle: () => void
  onSetRole: (role: 'admin' | 'member') => void
  onRemove: () => void
  onLeave: () => void
  onTransfer: () => void
  /** The server's own message. Rendered INSIDE the row, so it survives long
   *  enough to be read — a permission error must never be a toast. */
  error?: string | null
  busy: Busy
  /** From `staggerProps(i)` — the list entrance wave. */
  className?: string
  style?: React.CSSProperties
}) {
  const reduce = useReducedMotion()
  const [confirm, setConfirm] = useState<null | 'remove' | 'leave' | 'transfer'>(null)
  const isSelf = !!viewerUid && member.uid === viewerUid
  const isOwner = member.orgRole === 'owner'
  const label = member.displayName || member.email || member.uid
  const canManage = viewerRole === 'owner' || viewerRole === 'admin'
  // The server's rules, mirrored so we never draw a control it will refuse:
  // the owner is not removable by anyone, an admin may remove plain members
  // only, and anybody may remove themselves.
  const canRemove = !isOwner && !isSelf
    && (viewerRole === 'owner' || (viewerRole === 'admin' && member.orgRole === 'member'))
  const canChangeRole = viewerRole === 'owner' && !isOwner && !isSelf
  const canTransfer = viewerRole === 'owner' && !isOwner && member.status === 'active'
  const canLeave = isSelf && !isOwner
  // New, optional roster fields (not on OrgMemberRow yet — endpoints.ts). The
  // reason a seat is paused (for a precise chip), and the member's OWN personal
  // number, shown to an owner/admin when the row carries one. A member's number
  // stays THEIRS to edit (on their own /me/profile) — this is view-only here.
  const suspendedReason = (member as { suspendedReason?: string | null }).suspendedReason ?? null
  const personalPhone = (member as { personalPhone?: string | null }).personalPhone ?? null

  return (
    <li className={`relative ${className}`.trim()} style={style}>
      <PressScale
        as="div"
        feel={false}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={`member-actions-${member.uid}`}
        onClick={onToggle}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() }
        }}
        className="group flex min-h-[64px] w-full cursor-pointer items-center gap-3 px-1 py-3 text-left"
        data-testid={`team-member-${member.uid}`}
      >
        <Avatar label={label} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-medium text-ink">
            {label}
            {isSelf && <span className="ml-1.5 text-[12px] font-normal text-soft">(you)</span>}
          </span>
          <span
            className={`mt-0.5 block truncate text-[12.5px] ${TONE_CLASS[access.tone]}`}
            data-testid={`team-access-${member.uid}`}
          >
            {access.sentence}
          </span>
        </span>
        <VerifyChip status={member.status} suspendedReason={suspendedReason} access={access} />
        <RoleBadge role={member.orgRole} />
        <IconChevronRight
          size={17}
          className={`shrink-0 text-soft/70 transition-transform duration-default ${open ? 'rotate-90' : ''}`}
        />
      </PressScale>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`member-actions-${member.uid}`}
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduce ? { duration: 0.12 } : { duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-3 px-1 pb-4">
              {member.email && (
                <p className="truncate text-[12.5px] text-soft">{member.email}</p>
              )}

              {/* The member's OWN personal number, for an owner/admin to reach
                  them — view-only: it stays theirs to change on their own
                  profile. Shown only when the row carries one. */}
              {canManage && personalPhone && (
                <p className="truncate text-[12.5px] text-soft" data-testid={`team-member-phone-${member.uid}`}>
                  <span className="text-soft/70">Personal number: </span>
                  <a href={`tel:${personalPhone}`} className="font-medium text-ink hover:underline">
                    {personalPhone}
                  </a>
                </p>
              )}

              {error && (
                <p className="text-[13px] font-medium text-danger" role="alert" data-testid={`team-error-${member.uid}`}>
                  {error}
                </p>
              )}

              {isOwner && (
                <p className="text-[12.5px] leading-relaxed text-soft">
                  The owner can’t be removed. Transfer ownership first.
                </p>
              )}

              {canChangeRole && (
                <div>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-soft" id={`role-label-${member.uid}`}>
                    Role
                  </p>
                  <div
                    className="lqd-seg inline-flex rounded-full p-0.5"
                    role="group"
                    aria-labelledby={`role-label-${member.uid}`}
                    data-testid={`team-role-${member.uid}`}
                  >
                    {(['member', 'admin'] as const).map((r) => {
                      const active = member.orgRole === r
                      return (
                        <button
                          key={r}
                          type="button"
                          disabled={busy === 'role'}
                          aria-pressed={active}
                          onClick={() => { if (!active) { feedback.select(); onSetRole(r) } }}
                          className={`relative h-11 rounded-full px-4 text-[13px] font-semibold capitalize transition-colors focus-visible:shadow-[0_0_0_3px_var(--gold-300)] ${
                            active ? 'text-navy-900' : 'text-soft'
                          }`}
                          data-testid={`team-role-${member.uid}-${r}`}
                        >
                          {active && (
                            <motion.span
                              layoutId={`role-pip-${member.uid}`}
                              className="absolute inset-0 rounded-full bg-gold-500"
                              transition={{ type: 'spring', stiffness: 340, damping: 34 }}
                            />
                          )}
                          <span className="relative">{r}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {confirm === null && (
                <div className="flex flex-wrap gap-2">
                  {canTransfer && (
                    <PressScale
                      feel="tap"
                      onClick={() => setConfirm('transfer')}
                      className="btn-secondary h-11 px-4 text-[13px]"
                      data-testid={`team-transfer-${member.uid}`}
                    >
                      Transfer ownership…
                    </PressScale>
                  )}
                  {canRemove && (
                    <PressScale
                      feel="tap"
                      onClick={() => setConfirm('remove')}
                      className="inline-flex h-11 items-center rounded-full border border-danger/40 px-4 text-[13px] font-semibold text-danger"
                      data-testid={`team-remove-${member.uid}`}
                    >
                      Remove from team
                    </PressScale>
                  )}
                  {canLeave && (
                    <PressScale
                      feel="tap"
                      onClick={() => setConfirm('leave')}
                      className="inline-flex h-11 items-center rounded-full border border-danger/40 px-4 text-[13px] font-semibold text-danger"
                      data-testid={`team-leave-${member.uid}`}
                    >
                      Leave this organization
                    </PressScale>
                  )}
                  {!canManage && !canLeave && (
                    <p className="text-[12.5px] text-soft">
                      Only an owner or admin can change who is on the team.
                    </p>
                  )}
                </div>
              )}

              {confirm === 'remove' && (
                <DangerConfirm
                  prompt={`${label} loses access to ${orgName}’s prices and supplier contacts, and their seat comes back.`}
                  confirmLabel={`Remove ${label} from ${orgName}`}
                  busy={busy === 'remove'}
                  error={error}
                  onConfirm={onRemove}
                  onCancel={() => setConfirm(null)}
                  testId={`team-remove-confirm-${member.uid}`}
                />
              )}
              {confirm === 'leave' && (
                <DangerConfirm
                  prompt={`You’ll leave ${orgName}. You keep your account and can keep browsing — prices lock until you join a team again or are verified on your own.`}
                  confirmLabel={`Leave ${orgName}`}
                  busy={busy === 'remove'}
                  error={error}
                  onConfirm={onLeave}
                  onCancel={() => setConfirm(null)}
                  testId={`team-leave-confirm-${member.uid}`}
                />
              )}
              {confirm === 'transfer' && (
                <DangerConfirm
                  prompt={`${label} becomes the owner of ${orgName}.`}
                  confirmLabel="Continue"
                  secondPrompt={`Last check: ${label} will own ${orgName} — billing, seats and verification. You become an admin, and only they can hand it back.`}
                  secondConfirmLabel={`Make ${label} the owner`}
                  busy={busy === 'transfer'}
                  error={error}
                  onConfirm={onTransfer}
                  onCancel={() => setConfirm(null)}
                  testId={`team-transfer-confirm-${member.uid}`}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  )
}

/** A pending invite. A different lifecycle, a different verb, and it never
 *  pretends to be a person who can sign in. */
export function InviteRow({
  invite,
  access,
  canManage,
  open,
  onToggle,
  onRevoke,
  onResend,
  error,
  busy,
  className = '',
  style,
}: {
  invite: OrgInviteRow
  access: AccessVerdict
  canManage: boolean
  open: boolean
  onToggle: () => void
  onRevoke: () => void
  onResend: () => void
  error?: string | null
  busy: Busy
  className?: string
  style?: React.CSSProperties
}) {
  const reduce = useReducedMotion()
  const label = invite.email || 'Invited teammate'
  return (
    <li className={`relative ${className}`.trim()} style={style}>
      <PressScale
        as="div"
        feel={false}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={`invite-actions-${invite.inviteId}`}
        onClick={onToggle}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() }
        }}
        className="flex min-h-[64px] w-full cursor-pointer items-center gap-3 px-1 py-3 text-left"
        data-testid={`team-invite-${invite.inviteId}`}
      >
        <span
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-dashed border-gold-500/50 text-[12px] font-bold text-gold-600"
        >
          +
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-medium text-ink">{label}</span>
          <span
            className="mt-0.5 block truncate text-[12.5px] text-soft"
            data-testid={`team-access-${invite.inviteId}`}
          >
            {access.sentence}
          </span>
        </span>
        <RoleBadge role={invite.orgRole} pending />
        <IconChevronRight
          size={17}
          className={`shrink-0 text-soft/70 transition-transform duration-default ${open ? 'rotate-90' : ''}`}
        />
      </PressScale>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`invite-actions-${invite.inviteId}`}
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduce ? { duration: 0.12 } : { duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-3 px-1 pb-4">
              <p className="text-[12.5px] leading-relaxed text-soft">
                This invite holds a seat until it’s accepted, revoked or expires. We can’t show
                you the link — it exists only in the email we sent.
              </p>
              {error && (
                <p className="text-[13px] font-medium text-danger" role="alert" data-testid={`team-error-${invite.inviteId}`}>
                  {error}
                </p>
              )}
              {canManage ? (
                <div className="flex flex-wrap gap-2">
                  <PressScale
                    feel="tap"
                    disabled={busy === 'resend'}
                    onClick={onResend}
                    className="btn-secondary h-11 px-4 text-[13px]"
                    data-testid={`team-resend-${invite.inviteId}`}
                  >
                    {busy === 'resend' ? 'Sending…' : 'Send it again'}
                  </PressScale>
                  <PressScale
                    feel="dim"
                    disabled={busy === 'revoke'}
                    onClick={onRevoke}
                    className="inline-flex h-11 items-center rounded-full border border-danger/40 px-4 text-[13px] font-semibold text-danger"
                    data-testid={`team-revoke-${invite.inviteId}`}
                  >
                    {busy === 'revoke' ? 'Revoking…' : 'Revoke invite'}
                  </PressScale>
                </div>
              ) : (
                <p className="text-[12.5px] text-soft">Only an owner or admin can change invites.</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  )
}
